import { AppNode, AppLink, FunnelParameters, PresetFlow, OnboardingAnswers, ToolTemplate } from './types';

export const DEFAULT_PARAMETERS: FunnelParameters = {
  selfClarityScore: 0,
  educationalHours: 0,
  baseTicketPrice: 0,
  offerClarity: 0,
  traffic: 0,
  adSpend: 0,
  registrationRate: 0,
  chatEngagementRate: 0,
  showUpRate: 0,
  conversionRate: 0,
  retentionRate: 0,
  deliveryQuality: 0,
};

/**
 * Empty workspace. It is populated only by onboarding or direct user actions.
 */
export const PRESET_FLOWS: PresetFlow[] = [
  {
    name: 'Sin oferta',
    description: 'Crea una oferta o completa la configuración inicial para empezar.',
    parameters: { ...DEFAULT_PARAMETERS },
    nodes: [],
    links: []
  }
];

/**
 * LIENZO 2: Generador del Lienzo Personalizado a partir del Onboarding del Negocio
 */
export function generateCustomBusinessFlow(answers: OnboardingAnswers): PresetFlow {
  const projName = (answers.projectName || answers.companyName).trim();
  const ticket = Math.max(0, answers.ticketPrice || 0);
  const adSpend = Math.max(0, answers.monthlyAdBudgetUSD || 0);
  const selectedTools = Array.from(new Set(answers.selectedTools || []));
  const timestamp = Date.now();
  const stageY = { personal: 6, education: 16, offer: 27, attraction: 38, nurturing: 49, registration: 60, delivery: 71, attendance: 82, retention: 93 } as const;
  const toolDefinitions: Record<string, Pick<AppNode, 'name' | 'iconName' | 'stage'>> = {
    meta_ads: { name: 'Meta Ads', iconName: 'Megaphone', stage: 'attraction' },
    google_ads: { name: 'Google Ads', iconName: 'Search', stage: 'attraction' },
    linkedin_ads: { name: 'LinkedIn Ads', iconName: 'Linkedin', stage: 'attraction' },
    whatsapp_bot: { name: 'WhatsApp', iconName: 'MessageSquare', stage: 'nurturing' },
    email_nurture: { name: 'Email marketing', iconName: 'Mail', stage: 'nurturing' },
    landing_page: { name: 'Página de captación', iconName: 'Layout', stage: 'registration' },
    calendly: { name: 'Calendario de citas', iconName: 'Calendar', stage: 'registration' },
    payment_gateway: { name: 'Pasarela de pago', iconName: 'WalletCards', stage: 'offer' },
    lms_portal: { name: 'Portal de clientes', iconName: 'GraduationCap', stage: 'delivery' },
    zoom_webinar: { name: 'Videollamadas', iconName: 'Video', stage: 'attendance' },
    vip_community: { name: 'Comunidad', iconName: 'Users', stage: 'retention' },
  };
  const stageCounts = new Map<AppNode['stage'], number>();
  const customNodes = selectedTools.flatMap((toolId, index) => {
    const definition = toolDefinitions[toolId];
    if (!definition) return [];
    const stageIndex = stageCounts.get(definition.stage) || 0;
    stageCounts.set(definition.stage, stageIndex + 1);
    const node: AppNode = {
      id: `node-onboarding-${timestamp}-${index}`,
      ...definition,
      x: 50 + (stageIndex % 2 === 0 ? -1 : 1) * Math.ceil(stageIndex / 2) * 16,
      y: stageY[definition.stage],
    };
    if (definition.stage === 'attraction' && adSpend > 0) node.adBudgetUSD = adSpend;
    return [node];
  });
  const orderedNodes = [...customNodes].sort((a, b) => stageY[a.stage] - stageY[b.stage]);
  const customLinks: AppLink[] = orderedNodes.slice(1).map((node, index) => ({
    id: `link-onboarding-${timestamp}-${index}`,
    fromId: orderedNodes[index].id,
    toId: node.id,
  }));
  const descriptionParts = [answers.industry, answers.primaryGoal].filter(Boolean);

  return {
    name: projName,
    description: descriptionParts.join(' · '),
    parameters: {
      ...DEFAULT_PARAMETERS,
      baseTicketPrice: ticket,
      adSpend: adSpend,
    },
    nodes: orderedNodes,
    links: customLinks,
    industry: answers.industry
  };
}

export const AVAILABLE_APP_TEMPLATES: ToolTemplate[] = [
  // Personal Development
  { name: 'Clarity Coaching', iconName: 'Compass', stage: 'personal' as const },
  { name: 'Meditación Guiada', iconName: 'Heart', stage: 'personal' as const },
  { name: 'Rutina de Hábitos', iconName: 'Smile', stage: 'personal' as const },
  { name: 'Psicoterapia', iconName: 'Activity', stage: 'personal' as const },

  // Education
  { name: 'Curso Especializado', iconName: 'BookOpen', stage: 'education' as const },
  { name: 'Certificación Oficial', iconName: 'Award', stage: 'education' as const },
  { name: 'Taller Práctico', iconName: 'GraduationCap', stage: 'education' as const },
  { name: 'Práctica Supervisada', iconName: 'Layers', stage: 'education' as const },

  // Offer
  { name: 'Diseño High-Ticket', iconName: 'Coins', stage: 'offer' as const },
  { name: 'Copias Persuasivas', iconName: 'FileText', stage: 'offer' as const },
  { name: 'Ingeniería de Oferta', iconName: 'Zap', stage: 'offer' as const },
  { name: 'Taller Intensivo', iconName: 'Hammer', stage: 'offer' as const },

  // Attraction
  { name: 'Meta Ads', iconName: 'Megaphone', stage: 'attraction' as const },
  { name: 'LinkedIn Ads', iconName: 'Linkedin', stage: 'attraction' as const },
  { name: 'Google Search', iconName: 'Search', stage: 'attraction' as const },
  { name: 'Instagram Orgánico', iconName: 'Instagram', stage: 'attraction' as const },
  { name: 'Canal Telegram', iconName: 'Send', stage: 'attraction' as const },

  // Nurturing (Nutrición y Relación)
  { name: 'Sitio Web / Página Web', iconName: 'Globe', stage: 'nurturing' as const },
  { name: 'Blog de Contenidos', iconName: 'FileText', stage: 'nurturing' as const },
  { name: 'Newsletter Informativa', iconName: 'Newspaper', stage: 'nurturing' as const },
  { name: 'WhatsApp Bot', iconName: 'MessageSquare', stage: 'nurturing' as const },
  { name: 'Email Secuencia', iconName: 'Mail', stage: 'nurturing' as const },
  { name: 'SMS Recordatorios', iconName: 'MessageCircle', stage: 'nurturing' as const },
  { name: 'Grupo WhatsApp', iconName: 'Users', stage: 'nurturing' as const },

  // Registration
  { name: 'Grosym Landing', iconName: 'Layout', stage: 'registration' as const },
  { name: 'Formulario Web', iconName: 'ClipboardCheck', stage: 'registration' as const },
  { name: 'Calendly Agendamientos', iconName: 'Calendar', stage: 'registration' as const },
  { name: 'App de Reserva', iconName: 'PhoneCall', stage: 'registration' as const },

  // Delivery
  { name: 'LMS de Alumnos', iconName: 'GraduationCap', stage: 'delivery' as const },
  { name: 'Mentoría 1-a-1', iconName: 'Users', stage: 'delivery' as const },
  { name: 'Soporte Post-Venta', iconName: 'LifeBuoy', stage: 'delivery' as const },
  { name: 'Entrega Clínica', iconName: 'Activity', stage: 'delivery' as const },
  { name: 'Entrega de Proyecto', iconName: 'CheckSquare', stage: 'delivery' as const },

  // Attendance
  { name: 'Zoom Webinar', iconName: 'Video', stage: 'attendance' as const },
  { name: 'YouTube Live', iconName: 'Tv', stage: 'attendance' as const },
  { name: 'Telemedicina', iconName: 'Video', stage: 'attendance' as const },
  { name: 'Masterclass Viva', iconName: 'Sparkles', stage: 'attendance' as const },

  // Retention
  { name: 'Círculo VIP', iconName: 'Crown', stage: 'retention' as const },
  { name: 'Membresía Recurrente', iconName: 'Repeat', stage: 'retention' as const },
  { name: 'Club de Clientes', iconName: 'Users', stage: 'retention' as const },
  { name: 'Corte Ilimitado', iconName: 'Repeat', stage: 'retention' as const },
  { name: 'Newsletter Exclusiva', iconName: 'Newspaper', stage: 'retention' as const }
];
